import { OrderDetail as ApiOrderDetail } from "randmar-api-client";
import { IGeneralDocument, IInvoice, IOrderDetailResult, IPurchaseOrderPartNumberResult, IPurchaseOrderResult, IReceiptPartNumberResult, IReceiptResult, IRequisitionPartNumberResult, IRequisitionResult } from "../../api/document/DocumentsApi";
import { getCurrentDate } from "../../components/util/DateTimeUtil";
import Reseller from "../reseller/Reseller";

export interface IDocument {
  comments?: string;
  orderNumber?: string;
  Location?: string;
  purchaseOrderNumber?: string;
  receiptNumber?: string;
  requisitionNumber?: string;
  DocumentNumber?: string;
  SubTotal?: number;
}

export class Document {
  documentNumber?: string;
  location?: string;
  subTotal?: number;

  public static convertFrom(source: IDocument): Document {
    const document = new Document();

    document.documentNumber = source.DocumentNumber;
    document.location = source.Location;
    document.subTotal = source.SubTotal

    return document;
  }
}

export class PurchaseOrder implements IDocument {
  billToAddress1?: string;
  billToAddress2?: string;
  billToCity?: string;
  billToName?: string;
  billToPostalCode?: string;
  billToProvince?: string;
  comments?: string;
  contactEmail?: string;
  expectedArrivalDate?: number;
  location?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  purchaseOrderDate?: number;
  purchaseOrderNumber?: string;
  shipToAddress1?: string;
  shipToAddress2?: string;
  shipToCity?: string;
  shipToName?: string;
  shipToPostalCode?: string;
  shipToProvince?: string;
  shipVia?: string;
  shipViaDescription?: string;
  subTotal?: number;
  termsCode?: string;
  vendorCode?: string;
  vendorName?: string;


  partNumbers?: PurchaseOrderPartNumber[];
  receipts?: Receipt[];

  public static convertFrom(source: IPurchaseOrderResult): PurchaseOrder {
    const purchaseOrder = new PurchaseOrder();

    purchaseOrder.billToAddress1 = source.BillToAddress1;
    purchaseOrder.billToAddress2 = source.BillToAddress2;
    purchaseOrder.billToCity = source.BillToCity;
    purchaseOrder.billToName = source.BillToName;
    purchaseOrder.billToPostalCode = source.BillToPostalCode;
    purchaseOrder.billToProvince = source.BillToProvince;
    purchaseOrder.comments = source.Comments;
    purchaseOrder.contactEmail = source.ContactEmail;
    purchaseOrder.expectedArrivalDate = source.ExpectedArrivalDate;
    purchaseOrder.location = source.Location;
    purchaseOrder.manufacturerId = source.ManufacturerId;
    purchaseOrder.manufacturerName = source.ManufacturerName;
    purchaseOrder.purchaseOrderDate = source.PurchaseOrderDate;
    purchaseOrder.purchaseOrderNumber = source.PurchaseOrderNumber
    purchaseOrder.shipToAddress1 = source.ShipToAddress1;
    purchaseOrder.shipToAddress2 = source.ShipToAddress2;
    purchaseOrder.shipToCity = source.ShipToCity;
    purchaseOrder.shipToName = source.ShipToName;
    purchaseOrder.shipToPostalCode = source.ShipToPostalCode;
    purchaseOrder.shipToProvince = source.ShipToProvince;
    purchaseOrder.shipVia = source.ShipVia;
    purchaseOrder.shipViaDescription = source.ShipViaDescription;
    purchaseOrder.subTotal = source.SubTotal;
    purchaseOrder.termsCode = source.TermsCode;
    purchaseOrder.vendorCode = source.VendorCode;
    purchaseOrder.vendorName = source.VendorName;

    if (source.PartNumbers) {
      purchaseOrder.partNumbers = source.PartNumbers.map(PurchaseOrderPartNumber.convertFrom);
    }
    if (source.Receipts) {
      purchaseOrder.receipts = source.Receipts.map(Receipt.convertFrom);
    }


    return purchaseOrder;
  }
}

export class PurchaseOrderPartNumber {

  public randmarSKU?: string;
  public description?: string;
  public location?: string;
  public quantity?: number;
  public mpn?: string;
  public unitPrice?: number;
  public category?: string;
  public purchaseOrderNumber?: string;
  public purchaseOrderDate?: number;
  public lineNumber?: number;

  public static convertFrom(source: IPurchaseOrderPartNumberResult): PurchaseOrderPartNumber {
    const partNumber = new PurchaseOrderPartNumber();
    partNumber.randmarSKU = source.RandmarSKU;
    partNumber.description = source.Description;
    partNumber.location = source.Location;
    partNumber.mpn = source.MPN;
    partNumber.unitPrice = source.UnitPrice;
    partNumber.quantity = source.QuantityOutstanding;
    partNumber.category = source.Category;
    partNumber.purchaseOrderNumber = source.PurchaseOrderNumber;
    partNumber.purchaseOrderDate = source.PurchaseOrderDate;
    partNumber.lineNumber = source.LineNumber;

    return partNumber;
  }
}

export class Receipt implements IDocument {
  receiptNumber?: string;
  invoiceNumber?: string;
  location?: string;
  vendorName?: string;
  receiptDate?: number;
  purchaseOrderNumber?: string;
  comments?: string;
  orders?: OrderDetail[];
  partNumbers?: ReceiptPartNumber[];
  invoiced?: boolean;
  subTotal?: number;
  taxAmount1?: number;
  taxAmount2?: number;
  total?: number;

  public static convertFrom(source: IReceiptResult): Receipt {
    const receipt = new Receipt();

    receipt.invoiceNumber = source.InvoiceNumber;
    receipt.receiptNumber = source.ReceiptNumber;
    receipt.vendorName = source.VendorName;
    receipt.receiptDate = source.ReceiptDate;
    receipt.location = source.Location;
    receipt.purchaseOrderNumber = source.PurchaseOrderNumber;
    receipt.comments = source.Comments;
    receipt.invoiced = source.Invoiced;
    receipt.subTotal = source.SubTotal;
    receipt.taxAmount1 = source.TaxAmount1;
    receipt.taxAmount2 = source.TaxAmount2;
    receipt.total = source.Total;

    if (source.Orders)
      receipt.orders = source.Orders.map(o => OrderDetail.convertFrom(o));
    if (source.PartNumbers)
      receipt.partNumbers = source.PartNumbers.map(ReceiptPartNumber.convertFrom);

    return receipt;
  }
}

export class ReceiptPartNumber {

  public randmarSKU?: string;
  public description?: string;
  public location?: string;
  public quantity?: number;
  public receiptNumber?: string;
  public mpn?: string;
  public unitPrice?: number;

  public static convertFrom(source: IReceiptPartNumberResult): ReceiptPartNumber {
    const partNumber = new ReceiptPartNumber();
    partNumber.randmarSKU = source.RandmarSKU;
    partNumber.description = source.Description;
    partNumber.location = source.Location;
    partNumber.mpn = source.MPN;
    partNumber.unitPrice = source.UnitPrice;
    partNumber.receiptNumber = source.ReceiptNumber;
    partNumber.quantity = source.Quantity;
    return partNumber;
  }
}

export class Requisition implements IDocument {
  location?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  reference?: string;
  requestedBy?: string;
  requiredByDate?: number;
  requisitionDate?: number;
  requisitionNumber?: string;
  subTotal?: number;
  partNumbers?: RequisitionPartNumber[];

  public static convertFrom(source: IRequisitionResult): Requisition {
    const requisition = new Requisition();

    requisition.location = source.Location;
    requisition.manufacturerId = source.ManufacturerId;
    requisition.manufacturerName = source.ManufacturerName;
    requisition.reference = source.Reference;
    requisition.requestedBy = source.RequestedBy;
    requisition.requiredByDate = source.RequiredByDate;
    requisition.requisitionDate = source.RequisitionDate;
    requisition.requisitionNumber = source.RequisitionNumber;
    requisition.subTotal = source.SubTotal;

    if (source.PartNumbers)
      requisition.partNumbers = source.PartNumbers.map(RequisitionPartNumber.convertFrom);

    return requisition;
  }
}

export class RequisitionPartNumber {

  public randmarSKU?: string;
  public description?: string;
  public location?: string;
  public quantityOrdered?: number;
  public requisitionNumber?: string;
  public mpn?: string;
  public unitPrice?: number;

  public static convertFrom(source: IRequisitionPartNumberResult): RequisitionPartNumber {
    const partNumber = new RequisitionPartNumber();
    partNumber.randmarSKU = source.RandmarSKU;
    partNumber.description = source.Description;
    partNumber.location = source.Location;
    partNumber.mpn = source.MPN;
    partNumber.requisitionNumber = source.RequisitionNumber;
    partNumber.unitPrice = source.UnitPrice;
    partNumber.quantityOrdered = source.QuantityOrdered;
    return partNumber;
  }
}

export class Invoice implements IDocument {
  manufacturerId?: string;
  manufacturerName?: string;
  invoiceDate?: number;
  invoiceNumber?: string;
  purchaseOrderNumber?: string;
  dueDate?: number;
  taxAmount1?: number;
  taxRate1?: number;
  taxAmount2?: number;
  taxRate2?: number;
  subTotal?: number;
  total?: number;
  payed?: boolean;
  documentType?: string;

  public static convertFrom(source: IInvoice): Invoice {
    const invoice = new Invoice();

    invoice.manufacturerId = source.ManufacturerId;
    invoice.manufacturerName = source.ManufacturerName;
    invoice.invoiceDate = source.InvoiceDate;
    invoice.invoiceNumber = source.InvoiceNumber;
    invoice.purchaseOrderNumber = source.PurchaseOrderNumber;
    invoice.dueDate = source.DueDate;
    invoice.taxAmount1 = source.TaxAmount1;
    invoice.taxRate1 = source.TaxRate1;
    invoice.taxAmount2 = source.TaxAmount2;
    invoice.taxRate2 = source.TaxRate2;
    invoice.subTotal = source.SubTotal;
    invoice.total = source.Total;
    invoice.payed = source.Payed;
    invoice.documentType = source.DocumentType;

    return invoice;
  }
}

export class OrderDetail {
  category?: string;
  documentDate?: number;
  orderDate?: number;
  documentNumber?: string;
  documentType?: string;
  extendedPrice?: number;
  extendedWeight?: number;
  mpn?: string;
  manufacturerId?: string;
  opportunityManufacTurerId?: string;
  opportunityManufacturerNumber?: string;
  orderNumber?: string;
  poNumber?: string;
  manufacturerName?: string;
  quantity?: number;
  randmarSKU?: string;
  resellerId?: string;
  resellerName?: string;
  shipToName?: string;
  shipViaDescription?: string;
  unitPrice?: number;
  unitWeight?: number;
  warehouseCode?: string;
  shipToZipCode?: string;

  public static convertFrom(source: IOrderDetailResult): OrderDetail {
    return OrderDetail.convertFromApi(source as ApiOrderDetail);
  }

  public static convertFromApi(source: ApiOrderDetail): OrderDetail {
    const orderDetail = new OrderDetail();

    orderDetail.category = source.Category ?? undefined;
    orderDetail.orderDate = source.OrderDate;
    orderDetail.documentDate = source.DocumentDate;
    orderDetail.documentNumber = source.DocumentNumber ?? undefined;
    orderDetail.documentType = source.DocumentType ?? undefined;
    orderDetail.extendedPrice = source.ExtendedPrice;
    orderDetail.extendedWeight = source.ExtendedWeight;
    orderDetail.mpn = source.MPN ?? undefined;
    orderDetail.manufacturerId = source.ManufacturerId ?? undefined;
    orderDetail.opportunityManufacTurerId = source.OpportunityManufacTurerId ?? undefined;
    orderDetail.opportunityManufacturerNumber = source.OpportunityManufacturerNumber ?? undefined;
    orderDetail.orderNumber = source.OrderNumber ?? undefined;
    orderDetail.poNumber = source.PONumber ?? undefined;
    orderDetail.manufacturerName = source.ManufacturerName ?? undefined;
    orderDetail.quantity = source.Quantity;
    orderDetail.randmarSKU = source.RandmarSKU ?? undefined;
    orderDetail.resellerId = source.ResellerId ?? undefined;
    orderDetail.resellerName = source.ResellerName ?? undefined;
    orderDetail.shipToName = source.ShipToName ?? undefined;
    orderDetail.shipToZipCode = source.ShipToZipCode ?? undefined;
    orderDetail.shipViaDescription = source.ShipViaDescription ?? undefined;
    orderDetail.unitPrice = source.UnitPrice;
    orderDetail.unitWeight = source.UnitWeight;
    orderDetail.warehouseCode = source.WarehouseCode ?? undefined;

    return orderDetail;
  }

  public toApiType(): ApiOrderDetail {
    return {
      Category: this.category,
      DocumentDate: this.documentDate,
      DocumentNumber: this.documentNumber,
      DocumentType: this.documentType,
      ExtendedPrice: this.extendedPrice,
      ExtendedWeight: this.extendedWeight,
      MPN: this.mpn,
      ManufacturerId: this.manufacturerId,
      ManufacturerName: this.manufacturerName,
      OpportunityManufacTurerId: this.opportunityManufacTurerId,
      OpportunityManufacturerNumber: this.opportunityManufacturerNumber,
      OrderDate: this.orderDate,
      OrderNumber: this.orderNumber,
      PONumber: this.poNumber,
      Quantity: this.quantity,
      RandmarSKU: this.randmarSKU,
      ResellerId: this.resellerId,
      ResellerName: this.resellerName,
      ShipToName: this.shipToName,
      ShipToZipCode: this.shipToZipCode,
      ShipViaDescription: this.shipViaDescription,
      UnitPrice: this.unitPrice,
      UnitWeight: this.unitWeight,
      WarehouseCode: this.warehouseCode,
    };
  }

  public getColor(): string {

    if (this.warehouseCode === "3PLE") return "#FDF4DD";
    if (this.documentType === "Shipment") return "#E7F6E4";
    if (this.documentDate === getCurrentDate()) return "#E4F0F6";

    return "white";
  }
}

export class GeneralDocument {
  allowPartialShipment?: boolean;
  bidNumber?: string;
  endUserPO?: string;
  enteredBy?: string;
  manufacturerId?: string;
  orderDate?: string;
  activeOrderDetails?: OrderDetail[];
  completedSalesOrderDetails?: OrderDetail[];
  orderNumber?: string;
  pONumber?: string;
  resellerId?: number;
  shipAddress1?: string;
  shipAddress2?: string;
  shipCity?: string;
  shipContact?: string;
  shipCountry?: string;
  shipEmail?: string;
  shipName?: string;
  shippingSlipComment?: string
  shipProvince?: string;
  shipViaCode?: string;
  shipZip?: string;
  reseller?: Reseller;

  public static convertFrom(source: IGeneralDocument): GeneralDocument {
    const generalDocument = new GeneralDocument();

    generalDocument.allowPartialShipment = source.AllowPartialShipment;
    generalDocument.bidNumber = source.BidNumber;
    generalDocument.endUserPO = source.EndUserPO;
    generalDocument.enteredBy = source.EnteredBy;
    generalDocument.manufacturerId = source.ManufacturerId;
    generalDocument.orderDate = source.OrderDate;
    generalDocument.orderNumber = source.OrderNumber;
    generalDocument.pONumber = source.PONumber;
    generalDocument.resellerId = source.ResellerId;
    generalDocument.shipAddress1 = source.ShipAddress1;
    generalDocument.shipAddress2 = source.ShipAddress2;
    generalDocument.shipCity = source.ShipCity;
    generalDocument.shipContact = source.ShipContact;
    generalDocument.shipCountry = source.ShipCountry;
    generalDocument.shipEmail = source.ShipEmail;
    generalDocument.shipName = source.ShipName;
    generalDocument.shippingSlipComment = source.ShippingSlipComment;
    generalDocument.shipProvince = source.ShipProvince;
    generalDocument.shipViaCode = source.ShipViaCode;
    generalDocument.shipZip = source.ShipZip;

    if (source.Reseller)
      generalDocument.reseller = Reseller.convertFrom(source.Reseller);

    if (source.ActiveOrderDetails)
      generalDocument.activeOrderDetails = source.ActiveOrderDetails.map(o => OrderDetail.convertFrom(o));

    if (source.CompletedSalesOrderDetails)
      generalDocument.completedSalesOrderDetails = source.CompletedSalesOrderDetails.map(o => OrderDetail.convertFrom(o));

    return generalDocument;
  }
}
